import { supabase } from './supabase';
import pdfInvoiceService from './pdfInvoiceService';

class BillingService {
  constructor() {
    this.supabase = supabase;
  }

  // Get user's billing history from orders and transactions
  async getBillingHistory(options = {}) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      const userId = session.user.id;
      
      // Fetch transactions (all payment methods)
      const { data: transactions, error: transactionsError } = await this.supabase
        .from('transactions')
        .select(`
          *,
          invoices(invoice_number, description, issued_date)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (transactionsError) {
        console.error('Error fetching transactions:', transactionsError);
        return { success: false, error: 'Failed to fetch transactions' };
      }

      // Process transactions (All payment methods)
      const billingHistory = [];
      if (transactions) {
        transactions.forEach(transaction => {
          // Create better description for Razorpay transactions
          let description = transaction.invoices?.description || 'Payment';
          if (transaction.metadata?.plan && transaction.metadata?.billing_cycle) {
            description = `${transaction.metadata.plan} Plan - ${transaction.metadata.billing_cycle === 'monthly' ? 'Monthly' : 'Yearly'}`;
          }
          
          billingHistory.push({
            id: transaction.id,
            date: transaction.created_at,
            description: description,
            amount: parseFloat(transaction.amount),
            currency: transaction.currency,
            status: transaction.status === 'completed' ? 'paid' : transaction.status,
            invoice: transaction.invoices?.invoice_number || transaction.transaction_id,
            paymentMethod: transaction.payment_method || 'null', // Use actual payment method from database
            type: 'transaction',
            completedAt: transaction.processed_at
          });
        });
      }

      // Sort by date (most recent first)
      billingHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      console.log('Billing history data:', billingHistory);

      // Apply pagination if specified
      if (options.limit) {
        const startIndex = (options.page || 0) * options.limit;
        const endIndex = startIndex + options.limit;
        return {
          success: true,
          data: billingHistory.slice(startIndex, endIndex),
          total: billingHistory.length
        };
      }

      return {
        success: true,
        data: billingHistory,
        total: billingHistory.length
      };

    } catch (error) {
      console.error('Error getting billing history:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Get payment method information (if available)
  async getPaymentMethod() {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      // For now, return a placeholder since we don't store payment method details
      // In a real implementation, you might store this in a separate table
      return {
        success: true,
        data: {
          cardNumber: '**** **** **** 4242',
          expiryDate: '12/25',
          cardType: 'visa',
          isDefault: true
        }
      };

    } catch (error) {
      console.error('Error getting payment method:', error);
      return { success: false, error: error.message };
    }
  }

  // Download invoice as PDF
  async downloadInvoice(invoiceId) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      const userId = session.user.id;

      // Find the transaction by invoice ID - try multiple approaches
      let transactionData = null;

      // Approach 1: Try to find in transactions table by transaction_id
      const { data: transaction, error: transactionError } = await this.supabase
        .from('transactions')
        .select(`
          *,
          invoices(invoice_number, description, issued_date)
        `)
        .eq('user_id', userId)
        .eq('transaction_id', invoiceId)
        .single();

      if (!transactionError && transaction) {
        // Create better description for Razorpay transactions
        let description = transaction.invoices?.description || 'Payment';
        if (transaction.metadata?.plan && transaction.metadata?.billing_cycle) {
          description = `${transaction.metadata.plan} Plan - ${transaction.metadata.billing_cycle === 'monthly' ? 'Monthly' : 'Yearly'}`;
        }
        
        transactionData = {
          id: transaction.id,
          date: transaction.created_at,
          description: description,
          amount: parseFloat(transaction.amount),
          currency: transaction.currency,
          status: transaction.status === 'completed' ? 'paid' : transaction.status,
          invoice: transaction.invoices?.invoice_number || transaction.transaction_id,
          paymentMethod: transaction.payment_method || 'null', // Use actual payment method from database
          type: 'transaction',
          completedAt: transaction.processed_at
        };
      } else {
        // Approach 2: Try to find in transactions table by invoice_number
        const { data: transactionByInvoice, error: transactionByInvoiceError } = await this.supabase
          .from('transactions')
          .select(`
            *,
            invoices(invoice_number, description, issued_date)
          `)
          .eq('user_id', userId)
          .eq('invoices.invoice_number', invoiceId)
          .single();

        if (!transactionByInvoiceError && transactionByInvoice) {
          // Create better description for Razorpay transactions
          let description = transactionByInvoice.invoices?.description || 'Payment';
          if (transactionByInvoice.metadata?.plan && transactionByInvoice.metadata?.billing_cycle) {
            description = `${transactionByInvoice.metadata.plan} Plan - ${transactionByInvoice.metadata.billing_cycle === 'monthly' ? 'Monthly' : 'Yearly'}`;
          }
          
          transactionData = {
            id: transactionByInvoice.id,
            date: transactionByInvoice.created_at,
            description: description,
            amount: parseFloat(transactionByInvoice.amount),
            currency: transactionByInvoice.currency,
            status: transactionByInvoice.status === 'completed' ? 'paid' : transactionByInvoice.status,
            invoice: transactionByInvoice.invoices?.invoice_number || transactionByInvoice.transaction_id,
            paymentMethod: transactionByInvoice.payment_method || 'null', // Use actual payment method from database
            type: 'transaction',
            completedAt: transactionByInvoice.processed_at
          };
        }
      }

      if (!transactionData) {
        return { success: false, error: 'Invoice not found' };
      }

      // Get user profile data for the invoice
      const { data: userProfile, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const userData = profileError ? {} : {
        fullName: userProfile.full_name,
        email: userProfile.email,
        companyName: userProfile.company_name,
        address: userProfile.address
      };

      // Generate PDF invoice (now async)
      const pdf = await pdfInvoiceService.generateInvoice(transactionData, userData);
      
      // Create filename - add '_lut' suffix for USD transactions
      const date = new Date(transactionData.date).toISOString().split('T')[0];
      const isNonIndianTransaction = transactionData.currency === 'USD';
      const suffix = isNonIndianTransaction ? '_lut' : '';
      const filename = `invoice-${transactionData.invoice}-${date}${suffix}.pdf`;
      
      // Download the PDF
      pdfInvoiceService.downloadPDF(filename);

      return {
        success: true,
        message: 'Invoice downloaded successfully',
        filename: filename
      };

    } catch (error) {
      console.error('Error downloading invoice:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new BillingService();
